/**
 * CloudPath: the `cloud:<rel>` form every repo uses for a path in the user's cloud storage.
 *
 * Spec: omicsai-bioagent `docs/spec/cloud-path.md`; the shared test data is copied to
 * `__fixtures__/cloud-path-fixtures.json` and run by `cloudPath.test.ts`.
 *
 * - `<rel>` is relative to the user's cloud root (the S3 prefix `<identityId>/`), with no slash
 *   after the colon. Folders end in `/`; the root is `cloud:`.
 * - Parsing trims surrounding whitespace, collapses repeated `/` and drops `.` segments.
 * - It rejects anything not starting with lower-case `cloud:`, a slash after the colon, `..` or `~`
 *   segments, NUL characters and the empty string.
 * - Nothing is URL-encoded: spaces and non-ASCII characters are kept as they are.
 */

export const CLOUD_SCHEME = 'cloud:'

/** Root folder of the user's cloud storage. */
export const CLOUD_ROOT = CLOUD_SCHEME

/** Folder used when a writer has no inputs to place its output next to. */
const DEFAULT_OUTPUT_PARENT = `${CLOUD_SCHEME}OmicsAI/`

export class CloudPathError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CloudPathError'
  }
}

/** A parsed, canonical cloud path. */
export interface CloudPath {
  /** Canonical form, e.g. `cloud:chat_test/rnaseq/` */
  readonly cloud: string
  /** Path from the cloud root, e.g. `chat_test/rnaseq/` (`''` for the root) */
  readonly rel: string
  readonly isFolder: boolean
  readonly isRoot: boolean
}

function splitSegments(rest: string): {segments: string[]; isFolder: boolean} {
  const raw = rest.split('/')
  const segments: string[] = []
  for (const segment of raw) {
    if (segment === '..') throw new CloudPathError(`".." segments are not allowed: cloud:${rest}`)
    if (segment === '~') throw new CloudPathError(`"~" segments are not allowed: cloud:${rest}`)
    if (segment === '' || segment === '.') continue
    segments.push(segment)
  }
  const last = raw[raw.length - 1]
  return {segments, isFolder: segments.length === 0 || last === '' || last === '.'}
}

/** Parse and normalize a `cloud:` path. Throws {@link CloudPathError} when it is not one. */
export function parseCloudPath(input: string): CloudPath {
  if (typeof input !== 'string') throw new CloudPathError('Not a cloud: path')
  const trimmed = input.trim()
  if (!trimmed) throw new CloudPathError('Empty path')
  if (!trimmed.startsWith(CLOUD_SCHEME)) {
    throw new CloudPathError(`Not a cloud: path: ${trimmed}`)
  }
  const rest = trimmed.slice(CLOUD_SCHEME.length)
  if (rest.startsWith('/')) {
    throw new CloudPathError(`No slash after "cloud:", e.g. cloud:folder/file.txt (got ${trimmed})`)
  }
  if (rest.includes('\0')) throw new CloudPathError('NUL characters are not allowed')

  const {segments, isFolder} = splitSegments(rest)
  const isRoot = segments.length === 0
  const rel = isRoot ? '' : `${segments.join('/')}${isFolder ? '/' : ''}`
  return {cloud: `${CLOUD_SCHEME}${rel}`, rel, isFolder, isRoot}
}

/** {@link parseCloudPath}, or `null` when the input is not a valid cloud path. */
export function tryParseCloudPath(input: string): CloudPath | null {
  try {
    return parseCloudPath(input)
  } catch (error) {
    if (error instanceof CloudPathError) return null
    throw error
  }
}

export function isCloudPath(input: string): boolean {
  return tryParseCloudPath(input) !== null
}

/** Canonical `cloud:` form of a path (see the normalization rules above). */
export function normalizeCloudPath(input: string): string {
  return parseCloudPath(input).cloud
}

/** Path from the cloud root (`''` for the root). This is also the path inside `execute` code. */
export function cloudPathRel(input: string): string {
  return parseCloudPath(input).rel
}

export function isCloudFolder(input: string): boolean {
  return parseCloudPath(input).isFolder
}

export function isCloudRoot(input: string): boolean {
  return parseCloudPath(input).isRoot
}

/** Last segment, without a trailing `/` (`''` for the root). */
export function cloudPathName(input: string): string {
  const {rel} = parseCloudPath(input)
  const segments = rel.split('/').filter(Boolean)
  return segments.length ? segments[segments.length - 1] : ''
}

/** Containing folder (`cloud:` for top-level items), or `null` for the root itself. */
export function cloudPathParent(input: string): string | null {
  const {rel, isRoot} = parseCloudPath(input)
  if (isRoot) return null
  const segments = rel.split('/').filter(Boolean)
  const parentSegments = segments.slice(0, -1)
  return `${CLOUD_SCHEME}${parentSegments.map((s) => `${s}/`).join('')}`
}

/** Identity-prefixed S3 key: `<identityId>/<rel>`. */
export function cloudPathToS3Key(input: string, identityId: string): string {
  return `${identityId}/${parseCloudPath(input).rel}`
}

export function cloudPathToS3Uri(input: string, bucket: string, identityId: string): string {
  return `s3://${bucket}/${cloudPathToS3Key(input, identityId)}`
}

/**
 * Build a cloud path from a root-relative storage key (a file-table row id, a viewer's source path).
 * Leading slashes are dropped; `isFolder` forces the trailing `/`.
 */
export function cloudPathFromRel(rel: string, options: {isFolder?: boolean} = {}): string {
  const stripped = rel.replace(/^\/+/, '')
  const withSlash =
    options.isFolder && stripped && !stripped.endsWith('/') ? `${stripped}/` : stripped
  return parseCloudPath(`${CLOUD_SCHEME}${withSlash}`).cloud
}

/**
 * Reverse mapping from an identity-prefixed S3 key (or an `s3://bucket/…` URI) to `cloud:`.
 * A key under any other identity is an error.
 */
export function cloudPathFromS3Key(key: string, identityId: string): string {
  const withoutBucket = key.replace(/^s3:\/\/[^/]+\//, '')
  if (withoutBucket === identityId) return CLOUD_ROOT
  const prefix = `${identityId}/`
  if (!identityId || !withoutBucket.startsWith(prefix)) {
    throw new CloudPathError('S3 key is not under the signed-in identity')
  }
  return cloudPathFromRel(withoutBucket.slice(prefix.length))
}

/**
 * Where a writer puts its output when nobody named a location (spec "Default output folder",
 * location rule). `name` is the conversation's `outputFolderName`.
 */
export function defaultOutputLocation(name: string, inputs: readonly string[]): string {
  if (inputs.length === 0) return `${DEFAULT_OUTPUT_PARENT}${name}/`
  const first = parseCloudPath(inputs[0])
  const folder = first.isFolder ? first.cloud : (cloudPathParent(first.cloud) as string)
  // An input anywhere inside a previous output of this chat reuses that folder instead of
  // nesting another `<name>/` deeper down.
  for (let ancestor: string | null = folder; ancestor; ancestor = cloudPathParent(ancestor)) {
    if (cloudPathName(ancestor) === name) return ancestor
  }
  return `${folder}${name}/`
}
